import React, { useCallback, useEffect, useState } from 'react';
import { SaleTransactionStage, useGetSaleTransactionsQuery } from '../../../api';
import ListPagination from '../../../components/ListPagination';
import NoRecordsFound from '../../../components/NoRecordsFound';
import { FilterDialog, FilterMenu, SaleTransactionsBody } from '../../SaleTransactions/components';
import SearchHeader from './SearchHeader';

export type SalesTransactionsTabProps = {
    stages?: SaleTransactionStage[];
    title?: string;
    onlySelf?: boolean;
    showFilter?: boolean;
};

type ListContext = {
    search: string;
    page: number;
};

const pageSize = 9;

const SalesTransactionsTab = ({ stages, title, onlySelf, showFilter }: SalesTransactionsTabProps) => {
    const [listContext, setListContext] = useState<ListContext>({ search: '', page: 0 });
    const { page, search } = listContext;
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [stage, setStage] = useState<'all' | SaleTransactionStage>('all');
    const [createdAt, setCreatedAt] = useState<Date | null>(null);
    const [targetHandoverDate, setTargetHandoverDate] = useState<Date | null>(null);
    const [awardedDealer, setAwardedDealer] = useState<string>('all');

    const { data } = useGetSaleTransactionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            page: { limit: pageSize, offset: page * pageSize },
            filter: {
                stages: stages || (stage === 'all' ? null : [stage]),
                onlySelf,
                search,
                createdAt,
            },
        },
    });

    const setValues = useCallback(
        (salesStage, salesCreatedDate, targetHandoverDate, awardedDealer) => {
            setStage(salesStage);
            setCreatedAt(salesCreatedDate);
            setTargetHandoverDate(targetHandoverDate);
            setAwardedDealer(awardedDealer);
        },
        [setStage, setCreatedAt, setTargetHandoverDate, setAwardedDealer]
    );

    const searchOnChange = useCallback(
        (searchValue: string) => {
            setListContext(state => ({ ...state, search: searchValue }));
        },
        [setListContext]
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page]);

    const count = data?.saleTransactions?.count || 0;
    const saleTransactions = data?.saleTransactions?.items || [];

    return (
        <>
            <FilterDialog
                awardedDealer={awardedDealer}
                createdAt={createdAt}
                open={showFilterDialog}
                setOpen={setShowFilterDialog}
                setValues={setValues}
                stage={stage}
                targetHandoverDate={targetHandoverDate}
            />
            <FilterMenu
                awardedDealer={awardedDealer}
                createdAt={createdAt}
                open={showFilterMenu}
                setOpen={setShowFilterMenu}
                setValues={setValues}
                stage={stage}
                targetHandoverDate={targetHandoverDate}
            />
            <SearchHeader
                search={search}
                searchOnChange={searchOnChange}
                setShowFilterDialog={setShowFilterDialog}
                setShowFilterMenu={setShowFilterMenu}
                showFilter={showFilter}
                title={title}
            />
            {count === 0 ? (
                <NoRecordsFound />
            ) : (
                <>
                    <SaleTransactionsBody saleTransactions={saleTransactions} hideAwardedBitAmount hideAwardedDealer />
                    <ListPagination
                        activePage={page + 1}
                        count={count}
                        pageSize={pageSize}
                        setActivePage={(newPage: number) => setListContext({ ...listContext, page: newPage })}
                    />
                </>
            )}
        </>
    );
};

export default SalesTransactionsTab;
